/**
 * PURPOSE: Diálogos de elegibles Rugby (FTR-0015): plazo absoluto (partido o extensión por institución) y cambios extra.
 * RESPONSIBILITIES: Fecha, hora y minuto en hora de Lima y motivo (obligatorio cuando la acción lo exige: extensión /
 *   desbloqueo y cambios extra); confirmación de fechas pasadas. El servidor revalida todo; aquí solo se previene.
 * COLLABORATORS: CompanyRugbyLineupsTab.jsx, RugbyLineupSidePanel.jsx, uniformOptions.utils.js (hora de Lima).
 * ROUTE: winscore/src/admin/AdminCompany/RugbyLineupDialogs.jsx
 */
import React, { useState } from "react";

import { isPastLimaDateTime } from "./uniformOptions.utils.js";
import { POLICY_LIMITS } from "./rugbyLineups.utils.js";

const Overlay = ({ titleId, onCancel, onSubmit, children }) => (
  <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onCancel}>
    <form
      onSubmit={onSubmit}
      onMouseDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
      }}
      className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {children}
    </form>
  </div>
);

const Actions = ({ saving, onCancel, submitLabel }) => (
  <div className="mt-5 flex justify-end gap-2">
    <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
      Cancelar
    </button>
    <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300">
      {saving ? "Guardando..." : submitLabel}
    </button>
  </div>
);

/** Plazo absoluto: override del partido (motivo opcional salvo que ya venció) o extensión (motivo obligatorio). */
export const RugbyDeadlineDialog = ({ title, subtitle, initialLima = "", reasonRequired, saving, onCancel, onSubmit }) => {
  const [closesAtLima, setClosesAtLima] = useState(initialLima);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    if (!closesAtLima) return setError("Ingresa la fecha y la hora límite.");
    if (reasonRequired && !reason.trim()) return setError("El motivo es obligatorio.");
    if (isPastLimaDateTime(closesAtLima) && !window.confirm("Esa fecha y hora ya pasaron (hora de Lima): el envío quedará cerrado. ¿Guardar igualmente?")) {
      return undefined;
    }
    onSubmit({ closesAtLima, ...(reason.trim() ? { reason: reason.trim() } : {}) });
    return undefined;
  };
  return (
    <Overlay titleId="rugby-deadline-dialog-title" onCancel={onCancel} onSubmit={submit}>
      <h3 id="rugby-deadline-dialog-title" className="text-lg font-bold text-slate-800">{title}</h3>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      <label className="mt-4 block text-xs font-semibold text-slate-600">
        Fecha y hora límite (Lima)
        <input type="datetime-local" step={60} value={closesAtLima} onChange={(event) => setClosesAtLima(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
      </label>
      <label className="mt-3 block text-xs font-semibold text-slate-600">
        Motivo{reasonRequired ? " *" : " (obligatorio si el plazo vigente ya venció)"}
        <input type="text" maxLength={255} value={reason} onChange={(event) => setReason(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
      <Actions saving={saving} onCancel={onCancel} submitLabel="Guardar plazo" />
    </Overlay>
  );
};

/** Cambios extra: aditivos, con motivo obligatorio. */
export const RugbyGrantDialog = ({ subtitle, saving, onCancel, onSubmit }) => {
  const [granted, setGranted] = useState("1");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const submit = (event) => {
    event.preventDefault();
    const value = Number(granted);
    if (!Number.isInteger(value) || value < 1 || value > POLICY_LIMITS.GRANT_MAX) return setError(`Entero entre 1 y ${POLICY_LIMITS.GRANT_MAX}.`);
    if (!reason.trim()) return setError("El motivo es obligatorio.");
    onSubmit({ granted: value, reason: reason.trim() });
    return undefined;
  };
  return (
    <Overlay titleId="rugby-grant-dialog-title" onCancel={onCancel} onSubmit={submit}>
      <h3 id="rugby-grant-dialog-title" className="text-lg font-bold text-slate-800">Otorgar cambios extra</h3>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      <label className="mt-4 block text-xs font-semibold text-slate-600">
        Cambios adicionales
        <input type="number" min={1} max={POLICY_LIMITS.GRANT_MAX} value={granted} onChange={(event) => setGranted(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
      </label>
      <label className="mt-3 block text-xs font-semibold text-slate-600">
        Motivo *
        <input type="text" maxLength={255} value={reason} onChange={(event) => setReason(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-2 text-sm font-normal" />
      </label>
      {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
      <Actions saving={saving} onCancel={onCancel} submitLabel="Otorgar" />
    </Overlay>
  );
};
